import { z } from "zod";
import { oc } from "@orpc/contract";
import { IntegrationBaseSchema, IntegrationKey } from "./base";

/**
 * Plaid Bank Account Schema
 */
export const PlaidBankAccountSchema = z.object({
  id: z.string(), // unique ID for this connection
  accessToken: z.string(), // Plaid access token (will be obfuscated on frontend)
  itemId: z.string(), // Plaid item ID
  institutionName: z.string(), // Bank name
  institutionId: z.string(), // Plaid institution ID
  accountIds: z.array(z.string()), // Array of account IDs from this connection
  connectedAt: z.coerce.date(),
  checkbookBankId: z.string().optional(), // Cache Checkbook bank ID after first registration
});

/**
 * Cash Receipt Filter Config Schema
 * When allowedPaymentTypes is set and non-empty, it overrides the ACH-only
 * default filter used during bank transaction ingestion.
 */
export const CashReceiptFilterConfigSchema = z.object({
  allowedPaymentTypes: z.array(z.string()).optional(),
});

/**
 * Cash Receipt Filter Config Type
 */
export type CashReceiptFilterConfig = z.infer<
  typeof CashReceiptFilterConfigSchema
>;

/**
 * Plaid Integration Settings Schema
 */
export const PlaidIntegrationSettingsSchema = z.object({
  accounts: z.array(PlaidBankAccountSchema).prefault([]),
  cashReceiptFilter: CashReceiptFilterConfigSchema.optional(),
});

/**
 * Plaid Integration Settings Type
 */
export type PlaidIntegrationSettings = z.infer<
  typeof PlaidIntegrationSettingsSchema
>;

/**
 * Plaid Bank Account Type
 */
export type PlaidBankAccount = z.infer<typeof PlaidBankAccountSchema>;

/**
 * Plaid integration variant (without base fields)
 */
export const PlaidIntegrationSettingsSchemaWithoutBase = z.object({
  key: z.literal(IntegrationKey.Plaid),
  settings: PlaidIntegrationSettingsSchema,
});

/**
 * Full Plaid integration schema (with base fields)
 */
export const PlaidIntegrationSchema = IntegrationBaseSchema.merge(
  PlaidIntegrationSettingsSchemaWithoutBase,
);

/**
 * Plaid Integration Type
 */
export type PlaidIntegration = z.infer<typeof PlaidIntegrationSchema>;

/**
 * Create Plaid Link Token Contract
 */
const createPlaidLinkToken = oc
  .output(z.object({ linkToken: z.string(), expiration: z.coerce.date() }))
  .route({
    summary: "Start bank account linking",
    description:
      "Creates a short-lived Plaid Link token for the current user to connect a bank account. Returns the token and its expiry.",
    method: "POST",
  });

/**
 * Exchange Plaid Public Token Contract
 * Note: Returns PlaidIntegrationSchema directly to avoid circular dependency
 */
const exchangePlaidPublicToken = oc
  .input(
    z.object({
      publicToken: z.string(),
      institutionName: z.string(),
      institutionId: z.string(),
      accountIds: z.array(z.string()),
    }),
  )
  .output(PlaidIntegrationSchema)
  .route({
    summary: "Connect linked bank account",
    description:
      "Exchanges the Plaid public token from bank linking for an access token and saves the bank connection and selected accounts to the organization's Plaid integration.",
    method: "POST",
  });

/**
 * Delete Plaid Bank Account Contract
 * Note: Returns PlaidIntegrationSchema directly to avoid circular dependency
 */
const deletePlaidBankAccount = oc
  .input(z.object({ accountId: z.string() }))
  .output(PlaidIntegrationSchema)
  .route({
    summary: "Remove connected bank account",
    description:
      "Removes a bank connection from the organization's Plaid integration settings and returns the updated integration. Does not revoke access at Plaid.",
    method: "DELETE",
  });

/**
 * Create Plaid Update Mode Link Token Contract
 * Used for re-authenticating an existing bank connection
 */
const createPlaidUpdateLinkToken = oc
  .input(z.object({ accountId: z.string() }))
  .output(z.object({ linkToken: z.string(), expiration: z.coerce.date() }))
  .route({
    summary: "Reauthenticate bank connection",
    description:
      "Creates a Plaid Link token in update mode for an existing bank connection so the user can re-enter credentials when the bank login expires.",
    method: "POST",
  });

/**
 * Plaid Contracts
 */
export const plaidContracts = {
  createLinkToken: createPlaidLinkToken,
  exchangePublicToken: exchangePlaidPublicToken,
  deleteBankAccount: deletePlaidBankAccount,
  createUpdateLinkToken: createPlaidUpdateLinkToken,
} as const;

/**
 * Plaid Contracts Type
 */
export type PlaidContracts = typeof plaidContracts;
